import type { SandboxFile } from "@agent-ui/features/chat/hooks/useReploAgentConnection";
import type { Editor } from "@tiptap/core";

import * as React from "react";

import { ContextItemsMenu } from "@agent-ui/features/chat/components/chat-elements/context-items-menu/ContextItemsMenu";
import { useSessionUuid } from "@agent-ui/features/chat/hooks/useSessionUuid";
import { DevicePreview } from "@agent-ui/features/home/components/DevicePreview";
import PageSearchInput from "@agent-ui/features/page-renderer/components/navbar/PageSearchInput";
import PromptSuggestions from "@agent-ui/features/page-renderer/components/navbar/PromptSuggestions";
import { useSandboxFiles } from "@agent-ui/hooks/useSandboxFiles";
import { useSandboxPreviewBaseUrl } from "@agent-ui/store/sandboxNavigationStore";
import { Button } from "@replo/design-system/components/button/Button";
import { Empty } from "@replo/design-system/components/empty/Empty";
import {
  ModalContent,
  ModalRoot,
  ModalTrigger,
} from "@replo/design-system/components/modal/Modal";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import twMerge from "@replo/design-system/utils/twMerge";
import { useReploAgent } from "@v2/contexts/ReploAgentContext";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { Plus, Search } from "lucide-react";
import { useDebouncedState } from "replo-utils/react/use-debounced-state";

export function PagePreviewCard({
  file,
  previewBaseUrl,
  onClick,
}: {
  file: SandboxFile;
  previewBaseUrl: string | null;
  onClick: () => void;
}) {
  const [isHovered, setIsHovered] = React.useState(false);
  const previewUrl = previewBaseUrl
    ? `${previewBaseUrl}${file.route === "/" ? "" : file.route}`
    : undefined;

  return (
    <div
      className="flex flex-col rounded-lg border-0.5 border-border bg-white overflow-hidden cursor-pointer transition-all duration-150 hover:shadow-2xs"
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="group relative w-full aspect-[4/3] bg-sheet overflow-hidden p-2.5 pb-0">
        <div
          className={twMerge(
            "w-full h-[180%] transition-transform duration-[3s] ease-in-out rounded-t-sm overflow-hidden",
            isHovered ? "-translate-y-[35%]" : "translate-y-0",
          )}
        >
          <DevicePreview
            pageRoute={file.route}
            targetViewport="desktop"
            className="h-full w-full pointer-events-none"
            isScrollable={false}
            iframeSrc={previewUrl}
          />
        </div>
        <div
          className="absolute inset-0 bg-sheet/15 pointer-events-none opacity-0 transition-opacity duration-150 group-hover:opacity-100"
          aria-hidden
        />
        <div
          className="absolute bottom-0 left-0 right-0 h-16 pointer-events-none"
          style={{
            background:
              "linear-gradient(to bottom, transparent 0%, rgba(255,255,255,0.1) 25%, rgba(255,255,255,0.4) 50%, rgba(255,255,255,0.75) 75%, white 100%)",
          }}
          aria-hidden
        />
      </div>
      <div className="flex flex-col gap-0.5 px-2.5 py-2">
        <span className="text-xs font-medium truncate">{file.route}</span>
      </div>
    </div>
  );
}

function PageResultsHeader({ searchQuery }: { searchQuery: string }) {
  return (
    <div className="px-3 pt-3 pb-3">
      <span className="text-2xs font-medium text-muted uppercase tracking-wider">
        {searchQuery ? "Matching pages" : "Your pages"}
      </span>
    </div>
  );
}

function PageResults({
  searchQuery,
  onPageSelect,
  onSuggestionSelect,
  onSuggestionHover,
}: {
  searchQuery: string;
  onPageSelect: (file: SandboxFile) => void;
  onSuggestionSelect: (prompt: string) => void;
  onSuggestionHover: (prompt: string | null) => void;
}) {
  const projectId = useCurrentProjectId();
  const { agent } = useReploAgent();
  const previewBaseUrl = useSandboxPreviewBaseUrl();
  const sessionUuid = useSessionUuid();

  const { files, isLoading } = useSandboxFiles({
    agent,
    projectId: projectId ?? null,
    sessionUuid,
  });

  const query = searchQuery.toLowerCase();
  const filteredFiles = searchQuery
    ? files.filter((file) => {
        return file.route.toLowerCase().includes(query);
      })
    : files;

  const noMatchingPages = filteredFiles.length === 0 && searchQuery;

  if (noMatchingPages) {
    return (
      <PromptSuggestions
        searchQuery={searchQuery}
        onSelect={onSuggestionSelect}
        onHover={onSuggestionHover}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-col">
        <PageResultsHeader searchQuery={searchQuery} />
        <div className="p-3 pt-0">
          <div className="grid grid-cols-3 gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex flex-col gap-1.5">
                <Skeleton className="w-full aspect-[16/10] rounded" />
                <Skeleton className="h-3 w-20 rounded" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (filteredFiles.length === 0) {
    return (
      <div className="flex flex-col">
        <PageResultsHeader searchQuery={searchQuery} />
        <div className="p-3 pt-0">
          <div className="py-8 px-4 flex items-center justify-center">
            <Empty size="sm" icon={<Search size={14} />} title="No pages yet" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <PageResultsHeader searchQuery={searchQuery} />
      <div className="p-3 pt-0">
        <div className="grid grid-cols-3 gap-3">
          {filteredFiles.map((file) => (
            <PagePreviewCard
              key={file.route}
              file={file}
              previewBaseUrl={previewBaseUrl}
              onClick={() => onPageSelect(file)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function AddTabModalContent({
  onClose,
  onPageSelect,
  onPromptSubmit,
  isContextMenuOpen,
  onContextMenuOpenChange,
}: {
  onClose: () => void;
  onPageSelect: (file: SandboxFile) => void;
  onPromptSubmit: (prompt: string) => void;
  isContextMenuOpen: boolean;
  onContextMenuOpenChange: (newValue: boolean) => void;
}) {
  const [, searchQuery, setSearchInputValue] = useDebouncedState("", 250);
  const [tiptapEditor, setTiptapEditor] = React.useState<Editor | null>(null);
  const [hoveredSuggestion, setHoveredSuggestion] = React.useState<
    string | null
  >(null);

  return (
    <div className="flex flex-col w-full max-h-[80vh] overflow-clip">
      <PageSearchInput
        isContextMenuOpen={isContextMenuOpen}
        onContextMenuOpenChange={onContextMenuOpenChange}
        hoveredSuggestion={hoveredSuggestion}
        onHoveredSuggestionClear={() => setHoveredSuggestion(null)}
        onSubmit={(prompt) => {
          onPromptSubmit(prompt);
          onClose();
        }}
        onTextChange={setSearchInputValue}
        onEditorCreated={setTiptapEditor}
      />

      <div className="border-t-0.5 border-border" />

      {isContextMenuOpen ? (
        <ContextItemsMenu
          editor={tiptapEditor}
          hideHeader
          inputSize="base"
          onClose={() => {
            onContextMenuOpenChange(false);
          }}
        />
      ) : (
        <div className="overflow-y-auto flex-1 min-h-0">
          <PageResults
            searchQuery={searchQuery}
            onPageSelect={(file) => {
              onPageSelect(file);
              onClose();
            }}
            onSuggestionSelect={(prompt) => {
              setHoveredSuggestion(null);
              if (tiptapEditor) {
                tiptapEditor.commands.setContent(prompt, false);
                tiptapEditor.commands.focus("end");
                setSearchInputValue(prompt);
              }
            }}
            onSuggestionHover={setHoveredSuggestion}
          />
        </div>
      )}
    </div>
  );
}

function AddTabModal({
  onPageSelect,
  onPromptSubmit,
}: {
  onPageSelect: (file: SandboxFile) => void;
  onPromptSubmit: (prompt: string) => void;
}) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [isContextMenuOpen, setIsContextMenuOpen] = React.useState(false);

  return (
    <ModalRoot
      open={isOpen}
      onOpenChange={setIsOpen}
      disablePointerDismissal={false}
    >
      <ModalTrigger>
        <Button
          icon
          variant="tertiary"
          size="lg"
          aria-label="Open or create a page"
          UNSAFE_className="rounded-t-xl rounded-b-none hover:bg-white/60 focus-visible:outline-none focus-visible:ring-0"
        >
          <Plus size={14} />
        </Button>
      </ModalTrigger>
      <ModalContent
        showCloseButton={false}
        UNSAFE_className="w-[50vw] max-w-[calc(100vw-24px)] max-h-[80vh] min-h-[400px] rounded-xl p-0"
      >
        <AddTabModalContent
          isContextMenuOpen={isContextMenuOpen}
          onContextMenuOpenChange={setIsContextMenuOpen}
          onClose={() => setIsOpen(false)}
          onPageSelect={onPageSelect}
          onPromptSubmit={onPromptSubmit}
        />
      </ModalContent>
    </ModalRoot>
  );
}

export default AddTabModal;
